import React, { useCallback, useState } from "react"

import {
  Button,
  ColorSwatch,
  colorSwatchRingClassName,
  colorSwatchStyle,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from "@coval/ui"
import { ChevronDown } from "lucide-react"

import { TAG_COLOR_PALETTE } from "../constants/tag-colors"

interface TagColorSwatchPickerProps {
  tag: string
  color: string
  onColorChange: (color: string) => void
  onOpenChange?: (isOpen: boolean) => void
  disabled?: boolean
  isPending?: boolean
  variant?: "inline" | "field"
}

export const TagColorSwatchPicker: React.FC<TagColorSwatchPickerProps> = ({
  tag,
  color,
  onColorChange,
  onOpenChange,
  disabled,
  isPending,
  variant = "inline",
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const isUpdateDisabled = disabled || isPending

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      const resolved = isUpdateDisabled ? false : nextOpen
      setIsOpen(resolved)
      onOpenChange?.(resolved)
    },
    [isUpdateDisabled, onOpenChange],
  )

  const handleSelectColor = useCallback(
    (hex: string) => {
      if (isUpdateDisabled) {
        return
      }
      onColorChange(hex)
      handleOpenChange(false)
    },
    [isUpdateDisabled, onColorChange, handleOpenChange],
  )

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {variant === "field" ? (
          <Button
            type="button"
            variant="secondary"
            aria-label={`Change color for ${tag}`}
            disabled={isUpdateDisabled}
            className="w-full justify-between"
          >
            <ColorSwatch color={color} className="size-4 rounded-sm" />
            <ChevronDown />
          </Button>
        ) : (
          <button
            type="button"
            aria-label={`Change color for ${tag}`}
            disabled={isUpdateDisabled}
            className={`h-3 w-3 shrink-0 rounded-full ${colorSwatchRingClassName} focus-visible:outline-none disabled:opacity-60`}
            style={colorSwatchStyle(color)}
            onClick={(event) => event.stopPropagation()}
          />
        )}
      </PopoverTrigger>
      <PopoverContent side="bottom" sideOffset={4} className="w-[304px] p-3">
        <div className="flex flex-col gap-2">
          <div className="text-xs font-medium text-coval-text-muted">
            Choose a color
          </div>
          <div className="grid w-[280px] grid-cols-10 gap-1.5">
            {TAG_COLOR_PALETTE.map((hex) => {
              const isSelected = color.toLowerCase() === hex.toLowerCase()
              return (
                <Tooltip
                  key={hex}
                  content={hex}
                  triggerDisabled={isUpdateDisabled}
                >
                  <button
                    type="button"
                    disabled={isUpdateDisabled}
                    onClick={() => handleSelectColor(hex)}
                    // The selected swatch's ring is its selection indicator, so
                    // only unselected swatches take the contrast ring.
                    className={`h-6 w-6 cursor-pointer rounded transition-all hover:scale-110 hover:shadow-md focus:outline-none disabled:cursor-default disabled:opacity-60 disabled:hover:scale-100 disabled:hover:shadow-none ${
                      isSelected
                        ? "scale-110 shadow-md ring-2 ring-coval-info-border"
                        : colorSwatchRingClassName
                    }`}
                    style={colorSwatchStyle(hex)}
                    aria-label={`Select color ${hex}`}
                  />
                </Tooltip>
              )
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
